"use client";

import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";

interface ClickEffect {
  id: number;
  x: number;
  y: number;
  isButton: boolean;
}

interface PawPrint {
  id: number;
  x: number; // page X (includes scroll)
  y: number; // page Y (includes scroll)
  rotation: number;
}

// Wrapper to handle the 7 second delay before fading
// Uses absolute positioning to stay in place on the page
function PawPrintWithDelay({
  id,
  x,
  y,
  rotation,
  onRemove,
}: {
  id: number;
  x: number;
  y: number;
  rotation: number;
  onRemove: (id: number) => void;
}) {
  const [shouldExit, setShouldExit] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldExit(true);
    }, 7000);
    return () => clearTimeout(timer);
  }, []);

  if (shouldExit) {
    return (
      <motion.div
        className="absolute pointer-events-none text-primary/20"
        style={{
          left: x,
          top: y,
          transform: "translate(-50%, -50%)",
          fontSize: "24px",
          rotate: rotation,
        }}
        initial={{ opacity: 1, scale: 1 }}
        animate={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.5, ease: "easeIn" }}
        onAnimationComplete={() => onRemove(id)}
      >
        🐾
      </motion.div>
    );
  }

  return (
    <motion.div
      className="absolute pointer-events-none text-primary/20"
      style={{
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        fontSize: "24px",
      }}
      initial={{ opacity: 0, scale: 0.5, rotate: rotation - 10 }}
      animate={{ opacity: 1, scale: 1, rotate: rotation }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      🐾
    </motion.div>
  );
}

// Click ripple effect
function ClickRipple({
  x,
  y,
  isButton,
  onComplete,
}: {
  x: number;
  y: number;
  isButton: boolean;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="fixed pointer-events-none"
      style={{
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        zIndex: 99998,
      }}
      initial={{ scale: 0, opacity: 1 }}
      animate={{ scale: isButton ? 2 : 1.5, opacity: 0 }}
      transition={{ duration: isButton ? 0.4 : 0.3, ease: "easeOut" }}
      onAnimationComplete={onComplete}
    >
      {isButton ? (
        // Button click: paw burst
        <div className="relative">
          {[...Array(6)].map((_, i) => (
            <motion.span
              key={i}
              className="absolute text-primary"
              style={{
                fontSize: "12px",
                left: "50%",
                top: "50%",
              }}
              initial={{ x: 0, y: 0, opacity: 1 }}
              animate={{
                x: Math.cos((i * 60 * Math.PI) / 180) * 30,
                y: Math.sin((i * 60 * Math.PI) / 180) * 30,
                opacity: 0,
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              🐾
            </motion.span>
          ))}
        </div>
      ) : (
        // Empty click: simple ripple
        <motion.div
          className="w-8 h-8 rounded-full border-2 border-primary/50"
          initial={{ scale: 0.5, opacity: 0.8 }}
          animate={{ scale: 1.5, opacity: 0 }}
          transition={{ duration: 0.3 }}
        />
      )}
    </motion.div>
  );
}

// Trasy bez kursora-łapki (strony w stylu „Sky").
const DISABLED_PATHS = ["/", "/funkcjonalnosci", "/booking"];

export function PawCursor() {
  const pathname = usePathname();
  if (pathname && DISABLED_PATHS.includes(pathname)) return null;
  return <PawCursorInner />;
}

function PawCursorInner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(true);
  const [clickEffects, setClickEffects] = useState<ClickEffect[]>([]);
  const [pawPrints, setPawPrints] = useState<PawPrint[]>([]);

  const requestRef = useRef<number | null>(null);
  const targetPosition = useRef({ x: -100, y: -100 });
  const currentPosition = useRef({ x: -100, y: -100 });
  const cursorRef = useRef<HTMLDivElement>(null);
  const clickIdRef = useRef(0);
  const pawPrintIdRef = useRef(0);

  useEffect(() => {
    const checkTouch = () => {
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
      const isTouch = hasTouch && hasCoarsePointer;
      setIsTouchDevice(isTouch);
    };

    checkTouch();

    const isClickableElement = (el: Element | null): boolean => {
      if (!el) return false;
      const tag = el.tagName.toLowerCase();
      const isClickable =
        tag === "button" ||
        tag === "a" ||
        tag === "input" ||
        tag === "select" ||
        tag === "textarea" ||
        el.getAttribute("role") === "button" ||
        el.hasAttribute("onclick") ||
        el.closest("button") !== null ||
        el.closest("a") !== null ||
        el.closest('[role="button"]') !== null;
      return isClickable;
    };

    const handleMouseMove = (e: MouseEvent) => {
      targetPosition.current = { x: e.clientX, y: e.clientY };
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseEnter = () => {
      setIsVisible(true);
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Add click effect
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const isButton = isClickableElement(target);

      clickIdRef.current += 1;
      setClickEffects((prev) => [
        ...prev,
        {
          id: clickIdRef.current,
          x: e.clientX,
          y: e.clientY,
          isButton,
        },
      ]);

      // Add paw print on empty space clicks
      if (!isButton) {
        pawPrintIdRef.current += 1;
        const randomRotation = Math.random() * 40 - 20; // -20 to 20 degrees

        // Use pageX/pageY to get position relative to the document (includes scroll)
        setPawPrints((prev) => [
          ...prev,
          {
            id: pawPrintIdRef.current,
            x: e.pageX,
            y: e.pageY,
            rotation: randomRotation,
          },
        ]);
      }
    };

    // Smooth trailing animation - direct DOM manipulation (no React re-renders)
    const animate = () => {
      const cur = currentPosition.current;
      const tgt = targetPosition.current;
      cur.x += (tgt.x - cur.x) * 0.12;
      cur.y += (tgt.y - cur.y) * 0.12;

      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${cur.x - 10}px, ${cur.y - 10}px)`;
      }

      requestRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mousedown", handleMouseDown);
    document.documentElement.addEventListener("mouseleave", handleMouseLeave);
    document.documentElement.addEventListener("mouseenter", handleMouseEnter);
    requestRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mousedown", handleMouseDown);
      document.documentElement.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );
      document.documentElement.removeEventListener(
        "mouseenter",
        handleMouseEnter
      );
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [isVisible]);

  const removeClickEffect = (id: number) => {
    setClickEffects((prev) => prev.filter((effect) => effect.id !== id));
  };

  const removePawPrint = (id: number) => {
    setPawPrints((prev) => prev.filter((print) => print.id !== id));
  };

  if (isTouchDevice) return null;

  return (
    <>
      {/* Paw prints container - absolute positioning within the page */}
      <div className="absolute inset-0 overflow-visible pointer-events-none" style={{ zIndex: 1 }}>
        <AnimatePresence>
          {pawPrints.map((print) => (
            <PawPrintWithDelay
              key={print.id}
              id={print.id}
              x={print.x}
              y={print.y}
              rotation={print.rotation}
              onRemove={removePawPrint}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Click effects */}
      <AnimatePresence>
        {clickEffects.map((effect) => (
          <ClickRipple
            key={effect.id}
            x={effect.x}
            y={effect.y}
            isButton={effect.isButton}
            onComplete={() => removeClickEffect(effect.id)}
          />
        ))}
      </AnimatePresence>

      {/* Trailing paw - follows cursor with delay via direct DOM updates */}
      <div
        ref={cursorRef}
        className="fixed top-0 left-0 pointer-events-none select-none hidden md:block text-primary will-change-transform"
        style={{
          zIndex: 99999,
          fontSize: "20px",
          opacity: isVisible ? 0.7 : 0,
          transition: "opacity 0.15s",
        }}
      >
        🐾
      </div>
    </>
  );
}
